import { useEffect, useState } from "react";
import Switch from "@mui/material/Switch";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActors,
  selectSelectedElement,
  updateSelectedActorState,
} from "../../app/slices/TrainingSlice";
import { ActorState, ActorStateAnimation } from "../../models/ActorState";
import ActorStateLookatDropdown from "./ActorStateLookatDropdown";
import ActorStateOutfitDropdown from "./ActorStateOutfitDropdown";
import useActorStateProperties, {
  ActorStatus,
} from "./ActorStatePropertiesColumn.hooks";
import ActorStatePropertiesHeader from "./ActorStatePropertiesHeader";
import ActorStateMovementToggle from "./ActorStateMovementToggle";
import ActorStateMovementSelect from "./ActorStateMovementSelect";
import ActorStatePropInfoField from "./ActorStatePropInfoField";
import ActorStateAnimationSelector from "./ActorStateAnimationSelector";
import ActorStateSettingsTabs from "./ActorStateSettingsTabs";
import PiSwitch from "../../components/PiSwitch";
import ActorStateExploreIndicatorSettings, {
  SETTINGS_SWITCH_OFFSET,
} from "./ActorStateExploreIndicatorSettings";
import PiActorChipSelect from "../previews/components/PiActorChipSelect";
import PiLookModeChipSelect from "../previews/components/PiLookModeChipSelect";
import { PiSettingsList, PiSettingsRow } from "../previews/components/PiSettingsList";
import { hasLookatTarget } from "../../models/NoLookat";

export const FIRST_APPEARANCE_MOVEMENT_TOOLTIP =
  "This is the actor's first appearance, so they start at this position instead of moving to it.";

export default function ActorStatePropertiesEditor(props: {
  layout?: "stack" | "tiles";
}) {
  const { layout = "stack" } = props;
  const { t } = useTranslation();
  const selectedActorState = useSelector(selectSelectedElement) as
    | ActorState
    | undefined;
  const actors = useSelector(selectActors);
  const { getActorStatus } = useActorStateProperties();
  const [myActorStatus, setMyActorStatus] = useState<ActorStatus>(
    ActorStatus.Invalid
  );
  const dispatch = useDispatch();

  useEffect(() => {
    setMyActorStatus(getActorStatus());
  }, [selectedActorState, getActorStatus]);

  if (!selectedActorState) return null;

  const onChangeField = (field: keyof ActorState, value: any) => {
    dispatch(
      updateSelectedActorState({ ...selectedActorState, [field]: value })
    );
  };

  const onChangeAnimation = (animation: ActorStateAnimation) => {
    dispatch(updateSelectedActorState({ ...selectedActorState, animation }));
  };

  const canSelectMovement =
    (selectedActorState.actorId === "player" &&
      myActorStatus !== ActorStatus.Idle) ||
    (myActorStatus !== ActorStatus.Idle &&
      myActorStatus !== ActorStatus.Spawned);
  const movementDisabledReason =
    myActorStatus === ActorStatus.Spawned
      ? FIRST_APPEARANCE_MOVEMENT_TOOLTIP
      : undefined;
  // the look mode only means something once there is someone to look at
  const hasLookat = hasLookatTarget(selectedActorState.lookat);

  if (layout === "tiles") {
    // Design Studio actor-settings-layout/d-*: General / Appearance / Attachments tabs.
    const actor = actors.find((candidate) => candidate.id === selectedActorState.actorId);
    const general = (
      <PiSettingsList variant="open">
        <PiSettingsRow label={String(t("actor_settings.movement"))}>
          <ActorStateMovementSelect
            actor={selectedActorState}
            onChange={(movement) => onChangeField("movement", movement)}
            disabled={!canSelectMovement}
            disabledReason={movementDisabledReason}
          />
        </PiSettingsRow>
        {/* player Find: two extra rows in the same list, not stacked fields */}
        <ActorStateExploreIndicatorSettings
          key={selectedActorState.id}
          actorState={selectedActorState}
          disabled={!canSelectMovement}
          layout="rows"
          onChange={(changes) =>
            dispatch(updateSelectedActorState({ ...selectedActorState, ...changes }))
          }
        />
        <PiSettingsRow label={String(t("actor_settings.looks_at"))}>
          <PiActorChipSelect
            aria-label={String(t("actor_settings.looks_at"))}
            value={selectedActorState.lookat}
            onChange={(lookat) => onChangeField("lookat", lookat)}
            excludeActors={[selectedActorState.actorId]}
            tone="setting"
            allowKeepCurrent
            allowNoActor
          />
        </PiSettingsRow>
        <PiSettingsRow label={String(t("look_mode.label"))} disabled={!hasLookat}>
          <PiLookModeChipSelect
            value={selectedActorState.finalIK}
            onChange={(finalIK) => onChangeField("finalIK", finalIK)}
            disabled={!hasLookat}
          />
        </PiSettingsRow>
        <PiSettingsRow label={String(t("actor_settings.reset_parts"))}>
          <Switch
            checked={selectedActorState.resetCharacterParts ?? false}
            onChange={(_, checked) => onChangeField("resetCharacterParts", checked)}
            inputProps={{ "aria-label": String(t("actor_settings.reset_parts")) }}
            sx={{ ml: SETTINGS_SWITCH_OFFSET }}
          />
        </PiSettingsRow>
      </PiSettingsList>
    );

    return (
      <ActorStateSettingsTabs
        actorState={selectedActorState}
        actor={actor}
        general={general}
        onChangeOutfit={(outfit) => onChangeField("outfit", outfit)}
        onChangeAnimation={onChangeAnimation}
      />
    );
  }

  return (
    <div className="flex flex-col gap-[20px] py-[5px] px-[10px]">
      <ActorStatePropertiesHeader actor={selectedActorState} />
      <ActorStateMovementToggle
        actor={selectedActorState}
        onChange={(movement) => onChangeField("movement", movement)}
        disabled={!canSelectMovement}
        disabledReason={movementDisabledReason}
        variant="compact"
      />
      <ActorStateExploreIndicatorSettings
        key={selectedActorState.id}
        actorState={selectedActorState}
        disabled={!canSelectMovement}
        onChange={(changes) => dispatch(updateSelectedActorState({ ...selectedActorState, ...changes }))}
      />
      <ActorStateLookatDropdown
        actorState={selectedActorState}
        onChange={(lookat) => onChangeField("lookat", lookat)}
      />
      <div className="flex items-center gap-[12px]">
        <span
          className={hasLookat ? "text-[13px] text-creator-text-sub" : "text-[13px] text-creator-text-faded"}
        >
          {String(t("look_mode.label"))}
        </span>
        <PiLookModeChipSelect
          value={selectedActorState.finalIK}
          onChange={(finalIK) => onChangeField("finalIK", finalIK)}
          disabled={!hasLookat}
        />
      </div>
      <ActorStateAnimationSelector
        actorState={selectedActorState}
        onChange={onChangeAnimation}
      />
      <ActorStateOutfitDropdown
        actorState={selectedActorState}
        onChange={(outfit) => onChangeField("outfit", outfit)}
        disabled={myActorStatus === ActorStatus.Spawned}
      />
      <PiSwitch
        checked={selectedActorState.resetCharacterParts ?? false}
        label="Reset Character Parts"
        onChange={(checked) => onChangeField("resetCharacterParts", checked)}
      />
      <ActorStatePropInfoField
        actorState={selectedActorState}
        variant="compact"
      />
    </div>
  );
}
